import type { RecordingIntent } from "./api/client.ts";

export type RecordingPlayback = {
  readonly kind: "api-bytes";
  readonly intent: RecordingIntent;
  readonly recordingId: string;
  readonly objectUrl: string;
  release(): void;
};

const STORAGE_HOST = /blob\.core\.windows\.net/i;
const SAS_QUERY = /[?&]sig=/i;

export function sealPlayback(input: {
  recordingId: string;
  intent: RecordingIntent;
  objectUrl: string;
  release: () => void;
}): RecordingPlayback {
  if (STORAGE_HOST.test(input.objectUrl) || SAS_QUERY.test(input.objectUrl)) {
    throw new Error("storage URLs are not playback sources");
  }
  if (!input.objectUrl.startsWith("blob:")) {
    throw new Error("playback source must be an object URL from API bytes");
  }
  return {
    kind: "api-bytes",
    intent: input.intent,
    recordingId: input.recordingId,
    objectUrl: input.objectUrl,
    release: input.release,
  };
}

export async function loadRecording(input: {
  recordingId: string;
  intent: RecordingIntent;
  bytes: () => Promise<Blob>;
  createObjectUrl?: (blob: Blob) => string;
  revokeObjectUrl?: (url: string) => void;
}): Promise<RecordingPlayback> {
  const blob = await input.bytes();
  const createObjectUrl = input.createObjectUrl ?? URL.createObjectURL.bind(URL);
  const revokeObjectUrl = input.revokeObjectUrl ?? URL.revokeObjectURL.bind(URL);
  const objectUrl = createObjectUrl(blob);
  return sealPlayback({
    recordingId: input.recordingId,
    intent: input.intent,
    objectUrl,
    release() {
      revokeObjectUrl(objectUrl);
    },
  });
}
